/**
 * Frontend functionality for SVT Edit Media Modal
 */

import delegateListener from '../modules/delegate-listener';
import TemplateHelper from '../modules/template-helper';
import MediaItem from '../api/mediaitem';
import ModalHelper from '../modules/modal-helpers';
import InfoBar from '../modules/info-bar';

class EditMediaModal {
  constructor(options) {
    const defaults = {
      enabledPages: ['.page--edit-item-images'],
      modalTemplate: 'edit-image-modal',
      modalSelector: '[data-modal^="edit-media-"]',
      openerSelector: '.image-grid__edit',
      mediaItemSelector: '.image-grid__item',
      openerRoot: '.image-grid__container',
      deleteButtonSelector: '[data-remove-media]',
      undoButtonSelector: '[data-undo-media]',
      cropButtonSelector: '[data-crop-media]',
      shapesButtonSelector: '[data-shapes-media]',
      publishThumbSelector: '[data-item-id] > .image-grid__thumb',
      publishRadioSelector: '[data-publish-media] input[type="radio"]',
      delegateListener: delegateListener,
      templateHelper: new TemplateHelper(),
      modalHelper: new ModalHelper(),
      allMediaItemIds: [],
    };

    Object.assign(this, defaults, options);

    // Default location for info bar messages
    this.infoBarDefaults = {
      container: document.querySelector('.section--item-images'),
      group: 'media'
    };
  }

  /**
   * Attaches events if currently on an enabled page
   */
  init() {
    if(document.querySelectorAll(this.enabledPages.join(',')).length <= 0) {
      // short circuit initialization, as we're on a page that doesn't need this module.
      return;
    }
    this.modalHelper.init();

    document.querySelector(this.openerRoot).addEventListener(
      'click', delegateListener(
        this.openerSelector,
        this._handleOpenerClick.bind(this)
      )
    );

    document.body.addEventListener(
      'click', delegateListener(
        this.deleteButtonSelector,
        this._handleDeleteClick.bind(this)
      )
    );

    document.body.addEventListener(
      'change', delegateListener(
        this.publishRadioSelector,
        this._handlePublishChange.bind(this)
      )
    );

    document.body.addEventListener(
      'click', delegateListener(
        this.publishThumbSelector,
        this._handlePublishClick.bind(this)
      )
    );
  }

  /**
   * Event handler for delete media item button
   *
   * @param {Event} event - The click event
   */
  _handleDeleteClick(event) {
    // Find target mediaId
    const mediaId = event.delegateTarget.dataset.removeMedia;
    if(!mediaId) { throw Error('MediaID not found'); }

    const mediaItem = new MediaItem(mediaId);
    const res = mediaItem.delete();
    res.then(v => {
      this.removeMediaModals();
      this._removeItem(mediaId)
    });
  }

  /**
   * Removes a MediaItem's thumbnail from DOM
   *
   * @param {string} mediaId - The mediaId to be removed
   */
  _removeItem(mediaId) {
    const oldItem = Array.from(
      document.querySelectorAll(this.mediaItemSelector)
    ).filter(item => {
      return item.dataset.itemId === mediaId;
    })[0];
    if(oldItem) {
      oldItem.parentNode.removeChild(oldItem);
    }
  }

  /**
   * Event handler for MediaItem's published/unpublished status radio inputs
   * Finds the associated mediaItem ID and sets it's published state.
   *
   * @param {Event} event - Radio's change event (from delegate listener)
   */
  _handlePublishChange(event) {
    const radio = event.delegateTarget;
    const mediaId = radio.parentNode.dataset.publishMedia;
    if(!mediaId) {
      throw Error('Couldn\'t find media id dataset');
    }

    // Update thumbnail
    document.querySelector(
      `.image-grid__item[data-item-id="${ mediaId }"] .image-grid__thumb`
    ).classList.toggle('published', radio.value === '1');

    const mediaItem = new MediaItem(mediaId)
    mediaItem.publish(radio.value === '1');
  }

  /**
   * Event handler for MediaItem's published/unpublished status clicks
   * Finds the associated mediaItem ID and sets it's published state.
   *
   * @param {Event} event - Thumb's click event (from delegate listener)
   */
  _handlePublishClick(event) {
    this._triggerClearInfoEvent();
    const thumb = event.delegateTarget;
    const mediaId = parseInt(thumb.parentNode.dataset.itemId, 10);
    if(!mediaId) {
      throw Error('Couldn\'t find media id dataset');
    }

    const mediaItem = new MediaItem(mediaId);
    let imageData = window.IMAGES_DATA.filter(image => image.pid === mediaId)[0];

    // Store published state, if item isn't already in the list
    if(!imageData) {
      imageData = {
        pid: mediaId,
        published: thumb.classList.contains('published')
      };
      window.IMAGES_DATA.push(imageData);
    }

    const isPublished = imageData.published;

    mediaItem.publish(!isPublished).then(response => {
      if(response.status === 'OK') {
        imageData.published = !isPublished;
        thumb.classList.toggle('published', !isPublished);
      } else {
        this._showInfo(response.messages, 'error');
      }
    });
  }

  /**
   * Shorthand wrapper for displaying an infobar with item page's default settings
   *
   * @param {String|Array} text - The plaintext error message
   * @param {String} type - The infobar type [error/warning/check]
   */
  _showInfo(text, type) {
    if(Array.isArray(text)) {
      text = text.join(', ');
    }
    const infoBarOptions = Object.assign({}, this.infoBarDefaults, { text, type });
    new InfoBar(infoBarOptions);
  }

  /**
   * Triggers a custom item:clearInfos event
   */
  _triggerClearInfoEvent() {
    const event = new CustomEvent('item:clearInfos', {
      bubbles: true, detail: { group: this.infoBarDefaults.group }
    });
    this.infoBarDefaults.container.dispatchEvent(event);
  }

  /**
   * Creates and inserts an edit media modal into the DOM
   * TODO: Split into more manageable parts
   *
   * @param {int} mediaId - pid of the MediaItem
   */
  createModal(mediaId, { opener } = {}) {
    if(!opener) {
      opener = document.querySelector(`button[data-modal-target="edit-media-${mediaId}"]`);
    }
    if(opener) opener.disabled = true;
    const mediaItem = new MediaItem(mediaId);

    this.currentMediaId = mediaId;

    mediaItem.getHistory().then(response => {
      if(opener) opener.disabled = false;
      if(!response) return;
      if(response.status !== 'OK') {
        this._showInfo(response.messages, 'error');
        return;
      }

      const templateData = Object.assign({
        initiallyOpen: false
      }, response.result);

      templateData.historyItems = templateData.history;
      delete templateData.history;

      templateData.visibleInStore = Boolean(
        +templateData.visibleInStore
      );

      // Preprocess history items
      let lastDate = null;
      templateData.visibleHistoryItems = [];
      templateData.hiddenHistoryItems = [];
      templateData.historyItems.forEach((item, index) => {
        if(item.created_date !== lastDate) {
          item.visibleDate = true;
        }
        if(index < 2) {
          templateData.visibleHistoryItems.push(item);
        } else {
          templateData.hiddenHistoryItems.push(item);
        }
        lastDate = item.created_date;
      });

      // Add the data about previous and next buttons, are they needed
      const currentIndex = this.allMediaItemIds.indexOf(this.currentMediaId.toString());
      templateData.prevButton = currentIndex > 0;
      templateData.nextButton = currentIndex < this.allMediaItemIds.length - 1;

      const modalHTML = this.templateHelper.render(
        this.modalTemplate,
        templateData,
        document.body, 'append'
      );

      this.modalHelper.openModal(modalHTML);

      this.infoBarDefaults.container = document.querySelector('.modal--open .modal__container');

    });
  }

  /**
   * Removes all edit media modals from the DOM
   */
  removeMediaModals() {
    this.infoBarDefaults.container = document.querySelector(this.openerRoot);
    document.querySelectorAll(this.modalSelector).forEach(modal => {
      modal.parentNode.removeChild(modal);
    });

    // Unprevent scrolling
    document.body.style.overflow = '';
  }

  /**
   * Event handler for mediaItem's 'Edit' button
   * Removes existing media modals and loads the selected modal.
   *
   * @param {Event} event - The button's delegated event
   */
  _handleOpenerClick(event) {
    this._triggerClearInfoEvent();
    const mediaId = this._getAssociatedMediaId(event.delegateTarget);

    if(mediaId) {
      this.removeMediaModals();
      this.createModal(mediaId, { opener: event.delegateTarget });
    }
  }

  /**
   * Event handler for mediaItem's 'Next' and 'Previous' buttons
   * Navigates to the next or previous media item in the list
   * 
   * @param {String} direction - 'next' or 'prev'
   * 
   */
  _navigateMedia(direction) {
    const currentIndex = this.allMediaItemIds.indexOf(this.currentMediaId.toString());
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    const nextMediaId = this.allMediaItemIds[nextIndex];
    if(nextMediaId) {
      this.removeMediaModals();
      this.createModal(nextMediaId);
    }
  }

  /**
   * Finds a data attribute pointing to the media item pid
   *
   * @param {HTMLElement} target - the element from where to start searching
   *
   * @returns {int|boolean} - the found media item pid, or false if not found
   */
  _getAssociatedMediaId(target) {
    // HTMLElement.closest might need to be polyfilled for IE
    // https://plainjs.com/javascript/traversing/get-closest-element-by-selector-39/
    const item = target.closest(this.mediaItemSelector);
    if(item && typeof item.dataset.itemId !== 'undefined') {
      return parseInt(item.dataset.itemId, 10);
    }
    return false;
  }
}

module.exports = EditMediaModal;
